import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from '../static/styles/Highlights.module.css';
import playIcon from '../static/img/Highlights/iconPlayArrow.svg';
import { fetchGameVideos } from '../api/highlights';

import fifaIcon from '../static/img/DailyGamingTasks/iconFifaDailyTasks.avif';
import fifaImage from '../static/img/DailyGamingTasks/imageFifaGaming.avif';
import pubgIcon from '../static/img/DailyGamingTasks/iconPubgDailyTasks.png';
import pubgImage from '../static/img/DailyGamingTasks/imagePubgGaming.avif';
import freeFireIcon from '../static/img/DailyGamingTasks/iconFreeFireDailyTasks.avif';
import freeFireImage from '../static/img/DailyGamingTasks/imageFreeFireGaming.avif';
import apexImage from '../static/img/DailyGamingTasks/imageApexLegendsGaming.avif';
import codImage from '../static/img/DailyGamingTasks/imageCODGaming.avif';
import minecraftImage from '../static/img/DailyGamingTasks/imageMinecraftGaming.avif';
import clashOfClansImage from '../static/img/DailyGamingTasks/imageCOCGaming.avif';
import clashRoyaleImage from '../static/img/DailyGamingTasks/imageCRGaming.avif';
import fortniteImage from '../static/img/DailyGamingTasks/imageFortniteGaming.avif';
import amongUsImage from '../static/img/DailyGamingTasks/imageAmongUsGaming.avif';
import subwaySurfersImage from '../static/img/DailyGamingTasks/imageSubSurfGaming.avif';
import valorantImage from '../static/img/DailyGamingTasks/imageValorantGaming.avif';
import robloxImage from '../static/img/DailyGamingTasks/imageRobloxGaming.avif';

const getGameImage = (gameName) => {
    if (!gameName) return pubgImage;
    
    switch (gameName.toLowerCase()) {
        case 'pubg mobile':
        case 'pubg':
            return pubgImage;
        case 'freefire':
        case 'free fire':
            return freeFireImage;
        case 'fifa mobile':
        case 'fifa':
            return fifaImage;
        case 'apex legends':
        case 'apex':
            return apexImage;
        case 'call of duty':
        case 'cod':
            return codImage;
        case 'minecraft':
            return minecraftImage;
        case 'clash of clans':
        case 'coc':
            return clashOfClansImage;
        case 'clash royale':
        case 'cr':
            return clashRoyaleImage;
        case 'fortnite':
            return fortniteImage;
        case 'among us':
        case 'amongus':
            return amongUsImage;
        case 'subway surfers':
        case 'subwaysurfers':
            return subwaySurfersImage;
        case 'valorant':
            return valorantImage;
        case 'roblox':
            return robloxImage;
        default:
            return pubgImage; // Default image
    }
};

const GameHighlightsDetail = () => {
    const [searchParams] = useSearchParams();
    const gameId = searchParams.get('gameId');
    const gameName = searchParams.get('gameName')
    const [videos, setVideos] = useState([]);
    const [gameTitle, setGameTitle] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);


    useEffect(() => {
        document.title = `${gameName} Highlights | Jazz Esl`;
        const loadGameVideos = async () => {
            if (!gameId) {
                setError('No game ID provided');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                // console.log('Fetching videos for gameId:', gameId);
                const data = await fetchGameVideos(gameId);
                // console.log('Fetched videos:', data);
                setVideos(data);
                
                if (data && data.length > 0 && data[0].title) {
                        setGameTitle(gameName);
                }
            } catch (err) {
                setError(err.message || 'Failed to load game videos');
            } finally {
                setLoading(false);
            }
        };

        loadGameVideos();
    }, [gameId]);

    const handleVideoClick = (videoLink) => {
        if (videoLink) {
            const url = new URL(videoLink);
            if (url.hostname.includes('youtube.com') && url.searchParams.has('v')) {
                const videoId = url.searchParams.get('v');
                setSelectedVideo(`https://www.youtube.com/embed/${videoId}`);
            } else {
                setSelectedVideo(videoLink); // Fallback
            }
        }
    };

    const closeModal = () => {
        setSelectedVideo(null);
    };

    return (
        <div className={styles.container}>
            <h2 className={styles.heading}>{gameName || 'Game'} Highlights</h2>
            <p className={styles.subheading}>Watch the best {gameTitle || 'game'} highlights</p>
            
            {loading && <div className={styles.loading}>Loading...</div>}
            {error && <div className={styles.error}>Error: {error}</div>}
            
            {!loading && !error && videos.length === 0 && (
                <div className={styles.noVideos}>No videos found for this game</div>
            )}
            
            <div className={styles.cardGrid}>
                {videos.map((video) => (
                    <div 
                        key={video.id} 
                        className={styles.card}
                        onClick={() => handleVideoClick(video.link)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div 
                            className={styles.imageWrapper}
                            style={{ 
                                backgroundImage: video.thumbnail 
                                    ? `url(${video.thumbnail})` 
                                    : `url(${getGameImage(video.game || gameTitle)})`
                            }}
                        >
                            <div className={styles.playButtonWrapper}>
                                <img src={playIcon} alt="Play" className={styles.playButton} />
                            </div>
                            <div className={styles.overlay}></div>
                        </div>
                        <div className={styles.cardTitle}>{video.title}</div>
                    </div>
                ))}
            </div>

            {selectedVideo && (
                <div className={styles.modal}>
                    <div className={styles.modalContent}>
                        <button className={styles.closeButton} onClick={closeModal}>×</button>
                        <iframe 
                            src={selectedVideo} 
                            title="Video Player" 
                            frameBorder="0" 
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                            allowFullScreen 
                            className={styles.iframe}
                        ></iframe>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GameHighlightsDetail;